// @ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react'
import { getAuthFetchHeaders, API_BASE } from '../../utils/api'

const ago = (ms) => {
  if (!ms) return '—'
  const s = Math.floor((Date.now() - ms) / 1000)
  if (s < 2) return 'just now'
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  return `${Math.floor(s / 3600)}h ago`
}
const fmt = (n, unit = '') => {
  if (!n) return `0${unit}`
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M${unit}`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K${unit}`
  return `${n}${unit}`
}
const fmtBytes = (b) => {
  if (!b) return '0 B'
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`
  if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${b} B`
}
const fmtUptime = (sec) => {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
}
const sc = (ok) => ok == null ? '#6b7280' : ok ? '#10b981' : '#ef4444'
const nbg = (a) => a == null ? 'rgba(107,114,128,0.15)' : a ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)'
const nbd = (a) => a == null ? 'rgba(107,114,128,0.3)' : a ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'

function DataBadge({ source, ageMs }) {
  const stale = ageMs > 10000
  const c = stale ? '#f59e0b' : '#6b7280'
  return <span title={`Source: ${source}`} style={{ fontSize: '0.68rem', color: c, fontFamily: 'monospace', background: 'rgba(255,255,255,0.04)', padding: '1px 6px', borderRadius: 4, border: `1px solid ${c}22`, whiteSpace: 'nowrap' }}>{source} · {stale ? '⚠ stale' : ago(Date.now() - ageMs)}</span>
}

function SH({ title, extra }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 3, height: 12, background: '#6366f1', borderRadius: 2, display: 'inline-block' }} />{title}
      </div>
      {extra}
    </div>
  )
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '0.75rem' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ color: color || '#94a3b8', fontFamily: 'monospace' }}>{value}</span>
    </div>
  )
}

function MC({ label, value, sub, color }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px 14px', minWidth: 0 }}>
      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: color || '#f1f5f9', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 3 }}>{sub}</div>}
    </div>
  )
}

function InfraPipeline({ runtime, onNodeClick }) {
  const alive = !!runtime
  const flow = (runtime?.diagnostics?.wsTickStats?.messagesPerSec ?? 0) > 0
  const nodes = [
    { id: 'browser', label: 'Browser', sub: 'Client', ok: true },
    { id: 'cfpages', label: 'CF Pages', sub: 'CDN', ok: true },
    { id: 'worker', label: 'CF Worker', sub: runtime?.worker ? `DO ${runtime.worker.doPingMs ?? '?'}ms` : 'Worker', ok: !!runtime?.worker },
    { id: 'tunnel', label: 'Tunnel', sub: 'Cloudflared', ok: alive },
    { id: 'vps', label: 'Hostinger', sub: 'VPS', ok: alive },
    { id: 'engine', label: 'Engine', sub: runtime ? `v${runtime.version?.version}` : 'Trading', ok: alive },
  ]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
      {nodes.map((n, i) => (
        <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div onClick={() => onNodeClick(n.id)} style={{ background: nbg(n.ok), border: `1px solid ${nbd(n.ok)}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer', textAlign: 'center', minWidth: 82, transition: 'transform 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 2 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: sc(n.ok), display: 'inline-block', animation: n.ok ? 'nocPulse 2s infinite' : 'none' }} />
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>{n.label}</span>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>{n.sub}</div>
          </div>
          {i < nodes.length - 1 && (
            <svg width={28} height={16} viewBox="0 0 28 16">
              <line x1={0} y1={8} x2={20} y2={8} stroke={flow ? '#6366f1' : '#374151'} strokeWidth={2} strokeDasharray={flow ? '4 4' : 'none'} style={{ animation: flow ? 'dash 0.6s linear infinite' : 'none' }} />
              <polygon points="18,4 26,8 18,12" fill={flow ? '#6366f1' : '#374151'} />
            </svg>
          )}
        </div>
      ))}
    </div>
  )
}

function GreeksoftPanel({ runtime }) {
  const b = runtime?.brokers?.find(b => b.clientId === 'GREEK13') || runtime?.brokers?.[0] || null
  const ap = b?.apolloState, ir = b?.irisState
  const age = runtime ? Date.now() - runtime.measuredAt : 0
  const cards = [
    { id: 'REST', label: 'REST API', ok: b?.status === 'connected', st: b?.status?.toUpperCase() || 'N/A', lines: [`Polls: ${fmt(runtime?.diagnostics?.indicesPoller?.pollCount ?? 0)}`, runtime?.diagnostics?.indicesPoller?.lastError ? `Err: ${runtime.diagnostics.indicesPoller.lastError}` : 'No errors'] },
    { id: 'IRIS', label: 'IRIS WS', ok: ir?.wsOpen ?? false, st: ir?.wsOpen ? 'CONNECTED' : 'DISCONNECTED', lines: [`HB ACK: ${fmt(ir?.heartbeatAckCount ?? 0)}`, `Recon: ${ir?.reconnectCount ?? 0}`, ir?.lastDisconnectCode ? `Code: ${ir.lastDisconnectCode}` : ''] },
    { id: 'Apollo', label: 'Apollo WS', ok: ap?.wsOpen ? (ap.loginResponse === 'ok' ? true : null) : false, st: ap?.wsOpen ? (ap.loginResponse === 'ok' ? 'AUTHED' : 'OPEN') : 'DISCONNECTED', lines: [`Raw: ${fmt(ap?.rawFrameCount ?? 0)}`, `Parsed: ${fmt(ap?.parsedFrameCount ?? 0)}`, `Errs: ${ap?.parseErrorCount ?? 0}`] },
    { id: 'Contracts', label: 'Contracts', ok: (runtime?.diagnostics?.tickSymbols?.length ?? 0) > 0, st: (runtime?.diagnostics?.tickSymbols?.length ?? 0) > 0 ? 'LOADED' : 'IDLE', lines: [`Symbols: ${runtime?.diagnostics?.tickSymbols?.length ?? 0}`, runtime?.diagnostics?.tickSymbols?.slice(0, 3).join(' · ') || ''] },
    { id: 'HB', label: 'Heartbeat', ok: ap?.heartbeatStarted ?? false, st: ap?.heartbeatStarted ? 'ACTIVE' : 'IDLE', lines: [`Apollo: ${fmt(ap?.heartbeatAckCount ?? 0)} ACK`, `IRIS: ${fmt(ir?.heartbeatAckCount ?? 0)} ACK`, `Last: ${ago(ap?.lastHeartbeatAckAt)}`] },
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
      {cards.map(c => (
        <div key={c.id} style={{ background: nbg(c.ok), border: `1px solid ${nbd(c.ok)}`, borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#e2e8f0' }}>{c.label}</span>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: sc(c.ok), background: `${sc(c.ok)}22`, padding: '2px 6px', borderRadius: 4 }}>{c.st}</span>
          </div>
          {c.lines.filter(Boolean).map((l, i) => <div key={i} style={{ fontSize: '0.73rem', color: '#6b7280', lineHeight: 1.6 }}>{l}</div>)}
          {age > 0 && <div style={{ marginTop: 6 }}><DataBadge source="Runtime Object" ageMs={age} /></div>}
        </div>
      ))}
    </div>
  )
}

function MarketDataFlow({ runtime }) {
  const ap = runtime?.brokers?.[0]?.apolloState
  const ticks = runtime?.diagnostics?.wsTickStats
  const cf = runtime?.cloudflare
  const age = runtime ? Date.now() - runtime.measuredAt : 0
  const flow = (ticks?.messagesPerSec ?? 0) > 0
  const stages = [
    { label: 'Apollo WS', ok: !!ap?.wsOpen, lines: [`${fmt(ap?.rawFrameCount ?? 0)} frames`, `${ticks?.messagesPerSec ?? 0}/s`] },
    { label: 'Parser', ok: (ap?.parsedFrameCount ?? 0) > 0, lines: [`${fmt(ap?.parsedFrameCount ?? 0)} OK`, `${ap?.parseErrorCount ?? 0} err`] },
    { label: 'Cache', ok: (runtime?.diagnostics?.tickSymbols?.length ?? 0) > 0, lines: [`${runtime?.diagnostics?.tickSymbols?.length ?? 0} syms`, ticks?.lastTickAgeMs ? `${ticks.lastTickAgeMs}ms` : 'no data'] },
    { label: 'Emit', ok: (ticks?.totalTicks ?? 0) > 0, lines: [`${fmt(ticks?.totalTicks ?? 0)} total`, `${ticks?.ticksPerSec ?? 0}/s`] },
    { label: 'CF Push', ok: (cf?.pushCount ?? 0) > 0, lines: [`${fmt(cf?.pushCount ?? 0)} push`, `${cf?.lastPushLatencyMs ?? '?'}ms`] },
    { label: 'Browser', ok: (runtime?.diagnostics?.sseClientCount ?? 0) > 0, lines: [`${runtime?.diagnostics?.sseClientCount ?? 0} SSE`, ''] },
  ]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {stages.map((s, i) => (
        <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ background: nbg(s.ok), border: `1px solid ${nbd(s.ok)}`, borderRadius: 8, padding: '8px 12px', textAlign: 'center', minWidth: 80 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>{s.label}</div>
            {s.lines.map((l, j) => <div key={j} style={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.5 }}>{l}</div>)}
          </div>
          {i < stages.length - 1 && (
            <svg width={20} height={12}>
              <line x1={0} y1={6} x2={14} y2={6} stroke={flow ? '#6366f1' : '#374151'} strokeWidth={2} strokeDasharray={flow ? '3 3' : 'none'} style={{ animation: flow ? 'dash 0.5s linear infinite' : 'none' }} />
              <polygon points="12,3 18,6 12,9" fill={flow ? '#6366f1' : '#374151'} />
            </svg>
          )}
        </div>
      ))}
      {age > 0 && <div style={{ marginLeft: 8 }}><DataBadge source="Runtime Object" ageMs={age} /></div>}
    </div>
  )
}

function EventTimeline({ logs }) {
  const sev = s => s === 'error' ? '#ef4444' : s === 'warning' ? '#f59e0b' : s === 'success' ? '#10b981' : '#6b7280'
  return (
    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14, height: '100%', overflow: 'hidden' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Event Timeline</div>
      <div style={{ overflowY: 'auto', maxHeight: 240, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {[...logs].reverse().slice(0, 25).map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', padding: '3px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <span style={{ fontSize: '0.68rem', color: '#4b5563', flexShrink: 0, fontFamily: 'monospace' }}>{new Date(e.ts).toLocaleTimeString('en-IN', { hour12: false })}</span>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: sev(e.severity), flexShrink: 0, marginTop: 5 }} />
            <span style={{ fontSize: '0.7rem', color: '#64748b', flexShrink: 0 }}>[{e.subsystem}]</span>
            <span style={{ fontSize: '0.7rem', color: '#94a3b8', flex: 1, wordBreak: 'break-all' }}>{e.action}{e.detail ? ` — ${e.detail.slice(0, 70)}` : ''}</span>
          </div>
        ))}
        {logs.length === 0 && <div style={{ color: '#4b5563', fontSize: '0.75rem' }}>Waiting for events...</div>}
      </div>
      <div style={{ marginTop: 6 }}><DataBadge source="opsLog ring buffer" ageMs={0} /></div>
    </div>
  )
}

function PerfPanel({ runtime }) {
  const mem = runtime?.infrastructure?.process, w = runtime?.worker
  const hp = mem ? Math.round((mem.heapUsed / mem.heapTotal) * 100) : null
  return (
    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14, height: '100%' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Performance</div>
      <div style={{ fontSize: '0.68rem', color: '#4b5563', textTransform: 'uppercase', marginBottom: 4 }}>VPS Process</div>
      <Row label="RSS" value={mem ? `${(mem.rss / 1048576).toFixed(0)} MB` : '—'} />
      <Row label="Heap" value={mem ? `${(mem.heapUsed / 1048576).toFixed(0)} / ${(mem.heapTotal / 1048576).toFixed(0)} MB` : '—'} color={hp && hp > 85 ? '#ef4444' : undefined} />
      <Row label="Uptime" value={runtime ? fmtUptime(runtime.infrastructure.uptimeSec) : '—'} />
      <Row label="PID" value={mem?.pid?.toString() ?? '—'} />
      <div style={{ fontSize: '0.68rem', color: '#4b5563', textTransform: 'uppercase', margin: '10px 0 4px' }}>CF Worker</div>
      <Row label="DO Ping" value={w?.doPingMs != null ? `${w.doPingMs}ms` : '—'} />
      <Row label="KV Read" value={w?.kvReadMs != null ? `${w.kvReadMs}ms` : '—'} />
      <Row label="D1 Query" value={w?.d1QueryMs != null ? `${w.d1QueryMs}ms` : '—'} />
      {runtime && <div style={{ marginTop: 8 }}><DataBadge source="Runtime Object" ageMs={Date.now() - runtime.measuredAt} /></div>}
    </div>
  )
}

function ConfigPanel({ runtime }) {
  const r = runtime?.runtime
  return (
    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14, height: '100%' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Runtime Config</div>
      <Row label="HB Interval" value={r ? `${r.heartbeatInterval / 1000}s` : '—'} />
      <Row label="CF Push" value={r ? `${r.cfBridgeInterval}ms` : '—'} />
      <Row label="Version" value={runtime?.version?.version ?? '—'} />
      <Row label="Node" value={runtime?.version?.nodeVersion ?? '—'} />
      <Row label="Started" value={runtime?.version?.startedAt ? new Date(runtime.version.startedAt).toLocaleTimeString('en-IN') : '—'} />
      <Row label="CF Bridge" value={r?.cfBridgeUrl ? r.cfBridgeUrl.replace('https://', '').slice(0, 35) : '—'} />
      {runtime && <div style={{ marginTop: 8 }}><DataBadge source="Runtime Object" ageMs={Date.now() - runtime.measuredAt} /></div>}
    </div>
  )
}

function TestBtn({ component, authHeaders }) {
  const [state, setState] = useState(null), [loading, setLoading] = useState(false)
  const test = async () => {
    setLoading(true)
    try { const r = await fetch(`${API_BASE}/api/ops/test/${component}`, { method: 'POST', headers: authHeaders }); setState(await r.json()) }
    catch { setState({ status: 'FAIL', detail: 'Network error', durationMs: 0 }) }
    setLoading(false)
  }
  const c = state?.status === 'PASS' ? '#10b981' : state?.status === 'WARN' ? '#f59e0b' : '#ef4444'
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0' }}>
      <button onClick={test} disabled={loading} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#94a3b8', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'monospace' }}>{loading ? '...' : `Test ${component}`}</button>
      {state && <span style={{ fontSize: '0.7rem', color: c, fontFamily: 'monospace' }}>{state.status}{state.durationMs ? ` ${state.durationMs}ms` : ''}{state.detail ? ` — ${state.detail?.slice(0, 50)}` : ''}</span>}
    </div>
  )
}

function TracePanel({ authHeaders }) {
  const TOKENS = { NIFTY: '101999957', BANKNIFTY: '101999972', SENSEX: '100000034', BANKEX: '100000035', INDIAVIX: '999920005' }
  const [sym, setSym] = useState('NIFTY'), [trace, setTrace] = useState(null), [loading, setLoading] = useState(false), [age, setAge] = useState(null)
  const run = async () => {
    setLoading(true)
    const r = await fetch(`${API_BASE}/api/ops/trace/${TOKENS[sym] || sym}`, { headers: authHeaders })
    setTrace(await r.json()); setAge(Date.now()); setLoading(false)
  }
  const sc2 = s => ['CONNECTED', 'ACTIVE', 'HIT'].includes(s) ? '#10b981' : ['DISCONNECTED', 'MISS', 'NO_CLIENTS'].includes(s) ? '#ef4444' : '#f59e0b'
  return (
    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8' }}>🔍 Trace Market Tick</span>
        <select value={sym} onChange={e => setSym(e.target.value)} style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', borderRadius: 6, padding: '5px 10px', fontSize: '0.8rem' }}>
          {Object.keys(TOKENS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={run} disabled={loading} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#a5b4fc', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>{loading ? 'Tracing...' : 'Trace →'}</button>
        {age && <DataBadge source="Live Test" ageMs={Date.now() - age} />}
      </div>
      {trace && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {trace.pipeline?.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div title={p.detail} style={{ background: `${sc2(p.status)}15`, border: `1px solid ${sc2(p.status)}40`, borderRadius: 8, padding: '6px 10px', textAlign: 'center', cursor: 'help' }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 2 }}>{p.stage}</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: sc2(p.status) }}>{p.status}</div>
              </div>
              {i < trace.pipeline.length - 1 && <span style={{ color: '#374151' }}>→</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function RecorderPanel({ runtime, authHeaders }) {
  const [loading, setLoading] = useState(false), rec = runtime?.recorder
  const start = async () => { setLoading(true); await fetch(`${API_BASE}/api/ops/recorder/start`, { method: 'POST', headers: authHeaders }); setLoading(false) }
  const stop = async () => {
    setLoading(true)
    const d = await (await fetch(`${API_BASE}/api/ops/recorder/stop`, { method: 'POST', headers: authHeaders })).json()
    setLoading(false)
    if (d.downloadUrl) window.open(`${API_BASE}/api${d.downloadUrl}`, '_blank')
  }
  return (
    <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10, padding: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#a5b4fc', marginBottom: 4 }}>⏺ Runtime Recorder</div>
          <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
            {rec?.recording ? `🔴 Recording... ${rec.frameCount} frames` : rec?.stoppedAt ? `Last: ${rec.frameCount} frames` : 'Off — captures Apollo frames, IRIS events, REST calls (max 10 min)'}
          </div>
        </div>
        {!rec?.recording
          ? <button onClick={start} disabled={loading} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>▶ Start Recording</button>
          : <button onClick={stop} disabled={loading} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>⬛ Stop & Export</button>
        }
      </div>
    </div>
  )
}

export function AdminOpsCenter() {
  const [runtime, setRuntime] = useState(null)
  const [logs, setLogs] = useState([])
  const [logFilter, setLogFilter] = useState('all')
  const [logSearch, setLogSearch] = useState('')
  const [logPaused, setLogPaused] = useState(false)
  const [detailNode, setDetailNode] = useState(null)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  const termRef = useRef(null)
  const authHeaders = getAuthFetchHeaders()

  const fetchR = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/ops/runtime`, { headers: authHeaders })
      if (r.ok) { setRuntime(await r.json()); setLastFetch(Date.now()); setError(null) }
      else setError(`HTTP ${r.status}`)
    } catch (e) { setError(`Unreachable: ${e.message}`) }
  }, [])

  const fetchL = useCallback(async () => {
    if (logPaused) return
    try { const r = await fetch(`${API_BASE}/api/ops/logs?limit=200`, { headers: authHeaders }); if (r.ok) { const d = await r.json(); setLogs(d.entries || []) } } catch {}
  }, [logPaused])

  useEffect(() => {
    fetchR(); fetchL()
    const ri = setInterval(fetchR, 3000), li = setInterval(fetchL, 2000)
    return () => { clearInterval(ri); clearInterval(li) }
  }, [fetchR, fetchL])

  useEffect(() => { if (termRef.current && !logPaused) termRef.current.scrollTop = termRef.current.scrollHeight }, [logs, logPaused])

  const fl = logs.filter(l => {
    if (logFilter !== 'all' && l.subsystem?.toLowerCase() !== logFilter && l.severity !== logFilter) return false
    if (logSearch && !l.detail?.toLowerCase().includes(logSearch.toLowerCase()) && !l.action?.toLowerCase().includes(logSearch.toLowerCase())) return false
    return true
  }).slice(-200)

  const sevc = s => s === 'error' ? '#ef4444' : s === 'warning' ? '#f59e0b' : s === 'success' ? '#10b981' : '#3b82f6'
  const alive = !!runtime
  const ticks = runtime?.diagnostics?.wsTickStats
  const b0 = runtime?.brokers?.find(b => b.clientId === 'GREEK13') || runtime?.brokers?.[0] || null
  const ap = b0?.apolloState, ir = b0?.irisState

  const detailPanel = () => {
    if (!detailNode) return null
    const panels = {
      apollo: <div>
        <Row label="WS Open" value={ap?.wsOpen ? 'YES' : 'NO'} color={ap?.wsOpen ? '#10b981' : '#ef4444'} />
        <Row label="Login" value={ap?.loginResponse ?? '—'} color={ap?.loginResponse === 'ok' ? '#10b981' : ap?.loginResponse === 'error' ? '#ef4444' : undefined} />
        <Row label="Login Error Code" value={ap?.loginErrorCode?.toString() ?? '—'} color={ap?.loginErrorCode ? '#ef4444' : undefined} />
        <Row label="Raw Frames" value={fmt(ap?.rawFrameCount ?? 0)} />
        <Row label="Parsed" value={fmt(ap?.parsedFrameCount ?? 0)} />
        <Row label="Parse Errors" value={(ap?.parseErrorCount ?? 0).toString()} color={(ap?.parseErrorCount ?? 0) > 0 ? '#ef4444' : undefined} />
        <Row label="HB ACK" value={fmt(ap?.heartbeatAckCount ?? 0)} />
        <Row label="Reconnects" value={(ap?.reconnectCount ?? 0).toString()} />
        <Row label="Last Close" value={ap?.lastDisconnectCode?.toString() ?? '—'} />
        <Row label="First Tick" value={ap?.firstTickAt ? new Date(ap.firstTickAt).toLocaleTimeString('en-IN') : 'NEVER'} color={!ap?.firstTickAt ? '#ef4444' : '#10b981'} />
        <Row label="Last Tick" value={ap?.lastTickAt ? ago(ap.lastTickAt) : 'NEVER'} color={!ap?.lastTickAt ? '#f59e0b' : undefined} />
        <div style={{ fontSize: '0.68rem', color: '#4b5563', textTransform: 'uppercase', margin: '10px 0 4px' }}>Last Raw Frames (Pre-Parse)</div>
        {ap?.rawFrameLog?.slice(-3).map((f, i) => (
          <div key={i} style={{ margin: '4px 0', padding: '6px', background: 'rgba(0,0,0,0.4)', borderRadius: 6, fontSize: '0.7rem', fontFamily: 'monospace', color: '#6b7280' }}>
            <span style={{ color: '#4b5563' }}>#{f.frameNum}</span> · <span>{f.bytes}B</span> · <span>{new Date(f.ts).toLocaleTimeString('en-IN')}</span><br />
            <span style={{ wordBreak: 'break-all', color: '#4b5563' }}>{f.utf8Preview?.slice(0, 90)}</span>
          </div>
        ))}
        <TestBtn component="apollo" authHeaders={authHeaders} />
      </div>,
      iris: <div>
        <Row label="WS Open" value={ir?.wsOpen ? 'YES' : 'NO'} color={ir?.wsOpen ? '#10b981' : '#ef4444'} />
        <Row label="Login" value={ir?.loginResponse ?? '—'} />
        <Row label="Raw Frames" value={fmt(ir?.rawFrameCount ?? 0)} />
        <Row label="Order Responses" value={fmt(ir?.orderResponses ?? 0)} />
        <Row label="HB ACKs" value={fmt(ir?.heartbeatAckCount ?? 0)} />
        <Row label="Reconnects" value={(ir?.reconnectCount ?? 0).toString()} />
        <Row label="Last Close" value={ir?.lastDisconnectCode?.toString() ?? '—'} />
        <TestBtn component="iris" authHeaders={authHeaders} />
      </div>,
      engine: <div>
        <Row label="Version" value={runtime?.version?.version ?? '—'} />
        <Row label="Node" value={runtime?.version?.nodeVersion ?? '—'} />
        <Row label="SSE Clients" value={(runtime?.diagnostics?.sseClientCount ?? 0).toString()} />
        <Row label="Tick Status" value={ticks?.status ?? '—'} color={ticks?.status === 'LIVE' ? '#10b981' : ticks?.status === 'STALE' ? '#f59e0b' : '#ef4444'} />
        <Row label="Total Ticks" value={fmt(ticks?.totalTicks ?? 0)} />
        <TestBtn component="apollo" authHeaders={authHeaders} />
        <TestBtn component="iris" authHeaders={authHeaders} />
        <TestBtn component="sse" authHeaders={authHeaders} />
      </div>,
      vps: <div>
        <Row label="Uptime" value={runtime ? fmtUptime(runtime.infrastructure.uptimeSec) : '—'} />
        <Row label="RSS" value={fmtBytes(runtime?.infrastructure?.process?.rss ?? 0)} />
        <Row label="Heap" value={fmtBytes(runtime?.infrastructure?.process?.heapUsed ?? 0)} />
        <Row label="PID" value={(runtime?.infrastructure?.process?.pid ?? '—').toString()} />
        <TestBtn component="tunnel" authHeaders={authHeaders} />
      </div>,
      worker: <div>
        <Row label="DO Ping" value={runtime?.worker?.doPingMs != null ? `${runtime.worker.doPingMs}ms` : '—'} />
        <Row label="KV Read" value={runtime?.worker?.kvReadMs != null ? `${runtime.worker.kvReadMs}ms` : '—'} />
        <Row label="D1 Query" value={runtime?.worker?.d1QueryMs != null ? `${runtime.worker.d1QueryMs}ms` : '—'} />
        <TestBtn component="cf" authHeaders={authHeaders} />
      </div>,
      tunnel: <div><Row label="Status" value={alive ? 'HEALTHY' : 'DOWN'} color={alive ? '#10b981' : '#ef4444'} /><TestBtn component="tunnel" authHeaders={authHeaders} /></div>,
      cfpages: <div><Row label="Status" value="Always on (CF)" color="#10b981" /></div>,
      browser: <div><Row label="SSE Clients" value={(runtime?.diagnostics?.sseClientCount ?? 0).toString()} /><TestBtn component="sse" authHeaders={authHeaders} /></div>,
    }
    return (
      <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 340, zIndex: 1000, background: '#0f1117', borderLeft: '1px solid rgba(255,255,255,0.08)', padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, animation: 'slideIn 0.2s ease' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#e2e8f0', textTransform: 'capitalize' }}>{detailNode} Details</span>
          <button onClick={() => setDetailNode(null)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>
        {lastFetch && <DataBadge source="Runtime Object" ageMs={Date.now() - lastFetch} />}
        {panels[detailNode] ?? <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>No detail for this node.</div>}
      </div>
    )
  }

  return (
    <div className="admin-page" style={{ position: 'relative', paddingRight: detailNode ? 360 : 0, transition: 'padding-right 0.2s' }}>
      <style>{`@keyframes nocPulse{0%,100%{opacity:1}50%{opacity:0.4}} @keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}} @keyframes dash{to{stroke-dashoffset:-20}}`}</style>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 20, padding: '12px 16px', borderRadius: 12, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: '1rem', fontWeight: 800, color: '#e2e8f0' }}>⬡ LaMoney NOC</span>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: alive ? '#10b981' : '#ef4444', background: alive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${alive ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, padding: '3px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: alive ? '#10b981' : '#ef4444', animation: alive ? 'nocPulse 2s infinite' : 'none', display: 'inline-block' }} />
            {alive ? 'HEALTHY' : 'DEGRADED'}
          </span>
          <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>v{runtime?.version?.version ?? '—'}</span>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {[['Strategies', runtime?.strategies?.activeCount ?? 0], ['Brokers', runtime?.brokers?.length ?? 0], ['SSE', runtime?.diagnostics?.sseClientCount ?? 0], ['Ticks/s', ticks?.ticksPerSec ?? 0], ['CF Push', runtime?.cloudflare?.pushCount ?? 0]].map(([k, v]) => (
            <div key={k} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.68rem', color: '#4b5563', textTransform: 'uppercase' }}>{k}</div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#94a3b8' }}>{fmt(Number(v))}</div>
            </div>
          ))}
          {lastFetch && <DataBadge source="VPS Runtime" ageMs={Date.now() - lastFetch} />}
        </div>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: '0.8rem', color: '#ef4444' }}>⚠ {error} — retrying every 3s</div>}

      <SH title="Infrastructure Pipeline" />
      <div style={{ marginBottom: 16 }}><InfraPipeline runtime={runtime} onNodeClick={setDetailNode} /></div>

      <SH title="Greeksoft Stack" />
      <div style={{ marginBottom: 16 }}><GreeksoftPanel runtime={runtime} /></div>

      <SH title="Counters" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <MC label="Active Strategies" value={runtime?.strategies?.activeCount ?? 0} />
        <MC label="Total Ticks" value={fmt(ticks?.totalTicks ?? 0)} sub={`${ticks?.ticksPerSec ?? 0}/s`} />
        <MC label="Apollo Frames" value={fmt(ticks?.totalMessages ?? 0)} sub={`${ticks?.messagesPerSec ?? 0}/s`} />
        <MC label="CF Pushes" value={fmt(runtime?.cloudflare?.pushCount ?? 0)} sub={runtime?.cloudflare?.lastPushLatencyMs != null ? `${runtime.cloudflare.lastPushLatencyMs}ms` : '—'} />
        <MC label="Reconnects" value={runtime?.statistics?.totalReconnects ?? 0} color={(runtime?.statistics?.totalReconnects ?? 0) > 5 ? '#f59e0b' : undefined} />
        <MC label="Uptime" value={runtime ? fmtUptime(runtime.infrastructure.uptimeSec) : '—'} />
      </div>

      <SH title="Market Data Pipeline" />
      <div style={{ marginBottom: 16 }}><MarketDataFlow runtime={runtime} /></div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <EventTimeline logs={logs} />
        <PerfPanel runtime={runtime} />
        <ConfigPanel runtime={runtime} />
      </div>

      <SH title="System Logs" extra={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {['all', 'Apollo', 'IRIS', 'REST', 'CF', 'System', 'error'].map(f => (
            <button key={f} onClick={() => setLogFilter(f)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)', background: logFilter === f ? '#3b82f6' : 'rgba(255,255,255,0.04)', color: logFilter === f ? '#fff' : '#6b7280' }}>{f}</button>
          ))}
          <input type="text" placeholder="Search..." value={logSearch} onChange={e => setLogSearch(e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)', color: '#fff', fontSize: '0.75rem', width: 130, outline: 'none' }} />
          <button onClick={() => setLogPaused(p => !p)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: logPaused ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.04)', color: logPaused ? '#ef4444' : '#6b7280', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>
            {logPaused ? '▶ Resume' : '⏸ Pause'}
          </button>
        </div>
      } />
      <div ref={termRef} style={{ fontFamily: 'Consolas, Monaco, monospace', background: '#080b10', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14, height: 260, overflowY: 'auto', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fl.length === 0
          ? <div style={{ color: '#374151', textAlign: 'center', marginTop: 20, fontSize: '0.8rem' }}>No log entries matching filters.</div>
          : fl.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', borderLeft: `2px solid ${sevc(l.severity)}`, paddingLeft: 6, fontSize: '0.75rem' }}>
              <span style={{ color: '#374151', flexShrink: 0, fontSize: '0.72rem' }}>{new Date(l.ts).toLocaleTimeString('en-IN', { hour12: false })}</span>
              <span style={{ color: '#4b5563', flexShrink: 0, width: 48 }}>[{l.subsystem?.slice(0, 6)}]</span>
              <span style={{ color: sevc(l.severity), fontWeight: 600, flexShrink: 0, width: 52 }}>{l.severity?.toUpperCase()}</span>
              <span style={{ color: '#64748b', flexShrink: 0 }}>{l.action}</span>
              <span style={{ color: '#94a3b8', flex: 1, wordBreak: 'break-all' }}>{l.detail?.slice(0, 120)}</span>
            </div>
          ))
        }
      </div>

      <SH title="End-to-End Tick Trace" />
      <div style={{ marginBottom: 16 }}><TracePanel authHeaders={authHeaders} /></div>

      <SH title="Runtime Recorder" />
      <RecorderPanel runtime={runtime} authHeaders={authHeaders} />

      {detailPanel()}
    </div>
  )
}
